/**
 * Plan & Billing — M8 platform module.
 *
 * Current subscription card (status, trial/period dates, cancel) plus a
 * plans grid with entitlement checklists and one-click plan switching.
 *
 * Data: platformService (plans / subscription / changePlan /
 * cancelSubscription). MOCK_* fallbacks keep the page rendering without
 * a backend.
 */
import { useMemo } from 'react';
import { formatDateTime } from '../lib/datetime';
import TopBar from '../components/TopBar';
import { platformService } from '../lib/services';
import { useApi, useMutation } from '../lib/hooks';
import { useToast } from '../lib/toast';

const STATUS_PILL = {
  TRIALING: 'info', ACTIVE: 'success', PAST_DUE: 'danger', CANCELLED: '',
};

const fmtMoney = (cents) =>
  cents == null ? '—' : `A$${(cents / 100).toLocaleString('en-AU', { maximumFractionDigits: 0 })}`;
const fmtDate = (iso) => formatDateTime(iso);

/* ── Mock fallbacks: 3 plans + a TRIALING subscription ── */
const MOCK_PLANS = [
  {
    id: 'mock-p1', code: 'STARTER', name: 'Starter', monthlyPriceCents: 49900, active: true,
    entitlements: { maxUsers: 5, maxAiConversations: 500, voiceEnabled: false, marketingEnabled: false, apiAccess: false },
  },
  {
    id: 'mock-p2', code: 'GROWTH', name: 'Growth', monthlyPriceCents: 99000, active: true,
    entitlements: { maxUsers: 20, maxAiConversations: 3000, voiceEnabled: true, marketingEnabled: true, apiAccess: false },
  },
  {
    id: 'mock-p3', code: 'ENTERPRISE', name: 'Enterprise', monthlyPriceCents: 199000, active: true,
    entitlements: { maxUsers: -1, maxAiConversations: -1, voiceEnabled: true, marketingEnabled: true, apiAccess: true },
  },
];
const MOCK_SUBSCRIPTION = {
  id: 'mock-sub', planCode: 'GROWTH', status: 'TRIALING',
  trialEndsAt: '2026-08-01T00:00:00Z',
  currentPeriodStart: '2026-07-01T00:00:00Z',
  currentPeriodEnd: '2026-08-01T00:00:00Z',
};

const unlimitedOr = (v) =>
  v == null ? '—' : Number(v) < 0 ? 'Unlimited' : Number(v).toLocaleString('en-AU');

function EntitlementRow({ ok, label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0' }}>
      <span aria-hidden="true" style={{
        color: ok ? 'var(--success)' : 'var(--text-tertiary)',
        fontWeight: 700, width: 14, textAlign: 'center',
      }}>
        {ok ? '✓' : '—'}
      </span>
      <span style={{ fontSize: 12, color: ok ? 'var(--text-secondary)' : 'var(--text-tertiary)' }}>
        {label}
      </span>
    </div>
  );
}

export default function PlanBilling() {
  const { toast } = useToast();

  const { data: plans, refetch: refetchPlans } = useApi(
    () => platformService.plans(),
    { fallback: MOCK_PLANS, deps: [] }
  );
  const { data: subscription, refetch: refetchSub } = useApi(
    () => platformService.subscription(),
    { fallback: MOCK_SUBSCRIPTION, deps: [] }
  );

  const changePlan = useMutation(platformService.changePlan);
  const cancelSub  = useMutation(platformService.cancelSubscription);

  const planList = useMemo(
    () => (Array.isArray(plans) ? plans : plans?.content || []).filter((p) => p.active !== false),
    [plans]
  );
  const currentPlan = planList.find((p) => p.code === subscription?.planCode) || null;

  const refreshAll = () => { refetchPlans(); refetchSub(); };

  const onSwitch = async (plan) => {
    if (plan.code === subscription?.planCode) return;
    try {
      await changePlan.mutate(plan.code);
      toast(`Switched to the ${plan.name} plan`, { variant: 'success' });
      refreshAll();
    } catch (err) {
      toast(`Plan change failed: ${err?.message || 'unknown error'}`, { variant: 'danger' });
    }
  };

  const onCancel = async () => {
    if (!window.confirm('Cancel your subscription? You will keep access until the end of the current period.')) {
      return;
    }
    try {
      await cancelSub.mutate();
      toast('Subscription cancelled', { variant: 'warning' });
      refreshAll();
    } catch (err) {
      toast(`Cancel failed: ${err?.message || 'unknown error'}`, { variant: 'danger' });
    }
  };

  return (
    <>
      <TopBar
        crumbs={['Plan & Billing']}
        right={<button className="btn small" onClick={refreshAll}>↻ Refresh</button>}
      />
      <div className="app-content">
        <div className="page-header">
          <div>
            <div className="page-header-title">Plan &amp; Billing</div>
            <div className="page-header-sub">Subscription plan, entitlements and billing period</div>
          </div>
        </div>

        {/* ── Current subscription ── */}
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="flex-between" style={{ marginBottom: 12 }}>
            <span className="card-label" style={{ margin: 0 }}>CURRENT SUBSCRIPTION</span>
            <span className={`pill ${STATUS_PILL[subscription?.status] || ''} filled`}>
              {subscription?.status || 'UNKNOWN'}
            </span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 28, alignItems: 'flex-end' }}>
            <div>
              <div className="mono text-xs text-muted">PLAN</div>
              <div className="metric-value" style={{ fontSize: 24, color: 'var(--accent-primary)' }}>
                {currentPlan?.name || subscription?.planCode || '—'}
              </div>
              <div className="mono text-xs text-muted">
                {currentPlan ? `${fmtMoney(currentPlan.monthlyPriceCents)}/mo` : ''}
              </div>
            </div>
            {subscription?.status === 'TRIALING' && (
              <div>
                <div className="mono text-xs text-muted">TRIAL ENDS</div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{fmtDate(subscription?.trialEndsAt)}</div>
              </div>
            )}
            <div>
              <div className="mono text-xs text-muted">CURRENT PERIOD</div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>
                {fmtDate(subscription?.currentPeriodStart)} → {fmtDate(subscription?.currentPeriodEnd)}
              </div>
            </div>
            {subscription?.cancelledAt && (
              <div>
                <div className="mono text-xs text-muted">CANCELLED</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--danger)' }}>
                  {fmtDate(subscription.cancelledAt)}
                </div>
              </div>
            )}
            <div style={{ marginLeft: 'auto' }}>
              {subscription?.status !== 'CANCELLED' && (
                <button className="btn small danger" disabled={cancelSub.mutating} onClick={onCancel}>
                  {cancelSub.mutating ? 'Cancelling…' : 'Cancel subscription'}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ── Plans grid ── */}
        <div className="card-label" style={{ marginBottom: 10 }}>AVAILABLE PLANS</div>
        <div className="grid-3">
          {planList.map((plan) => {
            const ent = plan.entitlements || {};
            const isCurrent = plan.code === subscription?.planCode;
            return (
              <div
                key={plan.id || plan.code}
                className="card"
                style={{
                  border: isCurrent
                    ? '1px solid var(--accent-primary)'
                    : '1px solid var(--border-color)',
                  boxShadow: isCurrent ? '0 0 0 3px var(--accent-glow)' : undefined,
                  display: 'flex', flexDirection: 'column',
                }}
              >
                <div className="flex-between" style={{ marginBottom: 4 }}>
                  <span style={{ fontSize: 15, fontWeight: 700 }}>{plan.name}</span>
                  {isCurrent && <span className="pill accent filled">CURRENT</span>}
                </div>
                <div style={{ marginBottom: 12 }}>
                  <span className="metric-value" style={{ fontSize: 28, color: 'var(--accent-primary)' }}>
                    {fmtMoney(plan.monthlyPriceCents)}
                  </span>
                  <span className="mono text-xs text-muted"> /mo</span>
                </div>

                <div style={{ flex: 1, marginBottom: 12 }}>
                  <EntitlementRow ok label={`${unlimitedOr(ent.maxUsers)} users`} />
                  <EntitlementRow ok label={`${unlimitedOr(ent.maxAiConversations)} AI conversations /mo`} />
                  <EntitlementRow ok={!!ent.voiceEnabled}     label="Voice AI & call QA" />
                  <EntitlementRow ok={!!ent.marketingEnabled} label="Marketing campaigns & surveys" />
                  <EntitlementRow ok={!!ent.apiAccess}        label="API access" />
                </div>

                <button
                  className={`btn small ${isCurrent ? '' : 'primary'}`}
                  disabled={isCurrent || changePlan.mutating}
                  onClick={() => onSwitch(plan)}
                >
                  {isCurrent ? 'Current plan' : changePlan.mutating ? 'Switching…' : 'Switch to this plan'}
                </button>
              </div>
            );
          })}
          {planList.length === 0 && (
            <div className="card empty-state" style={{ padding: 24, textAlign: 'center' }}>
              <div className="text-muted">No plans available.</div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
